import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Download, BookOpen, Info, ArrowDownLeft, ArrowUpRight, Minus, Layers,
} from 'lucide-react'
import { PageHead } from '../AdminLayout'
import {
  Panel, DataTable, Pager, Toolbar, Select, SearchBox, RefreshButton, Tag, Stat,
} from '../ui'
import { CoinIcon } from '../../components/Market'
import { get, qs } from '../../lib/api'
import { fa, faDate, num, compact, asArray, clsx } from '../../lib/format'

const RANGES: [string, string][] = [
  ['0', 'از ابتدا'], ['1', 'امروز'], ['7', '۷ روز اخیر'],
  ['30', '۳۰ روز اخیر'], ['90', '۹۰ روز اخیر'],
]

const STATUSES: [string, string][] = [
  ['', 'همه وضعیت‌ها'], ['confirmed', 'تأیید شده'],
  ['pending', 'در انتظار'], ['rejected', 'رد شده'],
]

/** رنگ و نشان جهت حرکت دارایی */
function Direction({ d }: { d: number }) {
  if (d > 0) return <ArrowDownLeft className="h-3.5 w-3.5 text-up" />
  if (d < 0) return <ArrowUpRight className="h-3.5 w-3.5 text-down" />
  return <Minus className="h-3.5 w-3.5 text-slate-600" />
}

export default function Ledger() {
  const [days, setDays] = useState('0')
  const [currency, setCurrency] = useState('')
  const [op, setOp] = useState('')
  const [status, setStatus] = useState('')
  const [userId, setUserId] = useState('')
  const [page, setPage] = useState(1)
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  const params = { days, currency, op, status, user_id: userId, page, per_page: 50 }

  const load = () => {
    setLoading(true)
    get(`/admin/accounting/ledger-book${qs(params)}`)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }
  useEffect(load, [days, currency, op, status, userId, page])
  useEffect(() => setPage(1), [days, currency, op, status, userId])

  const rows = asArray<any>(data, 'data')
  const balances = asArray<any>(data?.balances)
  const totals = data?.totals || {}
  const ops = asArray<any>(data?.operations)

  const totalValue = balances.reduce((s: number, b: any) => s + (b.marketValue || 0), 0)
  const totalCost = balances.reduce((s: number, b: any) => s + (b.costValue || 0), 0)

  return (
    <>
      <PageHead
        title="دفتر کل دارایی و بهای تمام‌شده"
        subtitle="هر رویدادی که دارایی صرافی را جابه‌جا کرده، با موجودی قبل و بعد"
        action={
          <div className="flex gap-2">
            <a href={`/api/admin/accounting/ledger-book/export${qs(params)}`}
               className="btn-ghost !py-2 text-xs">
              <Download className="h-3.5 w-3.5" /> خروجی اکسل
            </a>
            <RefreshButton onClick={load} loading={loading} />
          </div>
        }
      />

      {data?.truncated && (
        <div className="card mb-4 flex items-start gap-3 border-gold/30 bg-gold/[.06] p-4">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
          <p className="text-xs leading-6 text-slate-200">
            تعداد رویدادها از سقف گزارش بیشتر شده و فقط تازه‌ترین‌ها بازپخش شده‌اند؛
            ستون موجودی برای ردیف‌های خیلی قدیمی ممکن است کامل نباشد.
          </p>
        </div>
      )}

      {/* ── خلاصه دارایی‌ها */}
      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="ارزش کل دارایی (نرخ روز)" color="#7C5CFF"
          icon={<Layers className="h-5 w-5" />} value={num(totalValue)}
          hint={`بهای تمام‌شده: ${compact(totalCost)} تومان`} />
        <Stat label="ورودی در این نما" color="#00E39A"
          icon={<ArrowDownLeft className="h-5 w-5" />} value={num(totals.inIrt || 0)}
          hint={`${num(totals.inQty || 0, 4)} واحد`} />
        <Stat label="خروجی در این نما" color="#FF4D6D"
          icon={<ArrowUpRight className="h-5 w-5" />} value={num(totals.outIrt || 0)}
          hint={`${num(totals.outQty || 0, 4)} واحد`} />
        <Stat label="ردیف‌های ثبت‌شده" color="#22D3EE"
          icon={<BookOpen className="h-5 w-5" />} value={fa(totals.count || 0)}
          hint={totals.pending ? `${fa(totals.pending)} ردیف تسویه‌نشده` : 'همه تسویه‌شده'} />
      </div>

      {/* ── موجودی هر دارایی */}
      <Panel dense className="mb-4" title="موجودی و بهای تمام‌شده هر دارایی">
        <DataTable
          rows={balances} loading={loading} minWidth={820}
          empty="هنوز حرکتی ثبت نشده است"
          columns={[
            {
              key: 'c', header: 'دارایی',
              cell: (r: any) => (
                <div className="flex items-center gap-2">
                  <CoinIcon symbol={r.currency} color={r.color} size={26} />
                  <div className="min-w-0">
                    <div className="truncate text-slate-100">{r.faName}</div>
                    <div className="num text-[11px] text-slate-500" dir="ltr">{r.currency}</div>
                  </div>
                </div>
              ),
            },
            { key: 'b', header: 'موجودی', cell: (r: any) => <span className="num text-slate-100">{num(r.balance, 6)}</span> },
            { key: 'cb', header: 'بهای تمام‌شده (هر واحد)', cell: (r: any) => <span className="num font-bold text-white">{num(r.costBasis)}</span> },
            { key: 'mp', header: 'نرخ روز', cell: (r: any) => <span className="num text-slate-300">{num(r.marketPrice)}</span> },
            { key: 'cv', header: 'ارزش به بهای تمام‌شده', cell: (r: any) => <span className="num text-slate-400">{compact(r.costValue)}</span> },
            { key: 'mv', header: 'ارزش به نرخ روز', cell: (r: any) => <span className="num font-semibold text-slate-100">{compact(r.marketValue)}</span> },
            {
              key: 'u', header: 'سود/زیان',
              cell: (r: any) => (
                <span className={clsx('num font-semibold',
                  r.unrealized > 0 ? 'text-up' : r.unrealized < 0 ? 'text-down' : 'text-slate-400')}>
                  {compact(r.unrealized)}
                </span>
              ),
            },
          ]}
        />
      </Panel>

      {/* ── دفتر */}
      <Panel dense title="دفتر رویدادها">
        <div className="p-4 pb-0">
          <Toolbar>
            <Select value={days} onChange={setDays} options={RANGES} />
            <Select value={currency} onChange={setCurrency}
              options={[['', 'همه دارایی‌ها'],
                        ...asArray<string>(data?.currencies).map((c) => [c, c] as [string, string])]} />
            <Select value={op} onChange={setOp}
              options={[['', 'همه عملیات'],
                        ...ops.map((o: any) => [o.value, o.label] as [string, string])]} />
            <Select value={status} onChange={setStatus} options={STATUSES} />
            <SearchBox value={userId} onChange={setUserId} placeholder="شناسه کاربر…" />
          </Toolbar>
        </div>

        <DataTable
          rows={rows} loading={loading} minWidth={1600}
          empty="رویدادی در این محدوده نیست"
          columns={[
            {
              key: 'at', header: 'تاریخ و ساعت',
              cell: (r: any) => <span className="whitespace-nowrap text-slate-300">{faDate(r.at)}</span>,
            },
            {
              key: 'user', header: 'کاربر',
              cell: (r: any) => (
                r.userId
                  ? <Link to={`/admin/users/${r.userId}`} className="num text-brand-300 hover:underline" dir="ltr">
                      {r.email}
                    </Link>
                  : <span className="text-slate-500">{r.email}</span>
              ),
            },
            {
              key: 'op', header: 'نوع عملیات',
              cell: (r: any) => (
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  <Direction d={r.direction} />
                  <span className="text-slate-200">{r.opLabel}</span>
                </span>
              ),
            },
            {
              key: 'cur', header: 'دارایی',
              cell: (r: any) => (
                <span className="flex items-center gap-1.5">
                  <CoinIcon symbol={r.currency} color={r.color} size={20} />
                  <span className="num text-slate-200" dir="ltr">{r.currency}</span>
                </span>
              ),
            },
            { key: 'qty', header: 'مقدار', cell: (r: any) => <span className="num text-slate-100">{num(r.qty, 8)}</span> },
            { key: 'price', header: 'قیمت', cell: (r: any) => <span className="num text-slate-300">{r.price ? num(r.price) : '—'}</span> },
            { key: 'val', header: 'ارزش ریالی', cell: (r: any) => <span className="num text-slate-300">{r.valueIrt ? num(r.valueIrt) : '—'}</span> },
            {
              key: 'fee', header: 'کارمزد',
              cell: (r: any) => (
                r.fee
                  ? <span className="num text-gold">{num(r.fee, 8)} <span className="text-[10px] text-slate-500">{r.feeCurrency}</span></span>
                  : <span className="text-slate-600">—</span>
              ),
            },
            { key: 'cb', header: 'بهای تمام‌شده', cell: (r: any) => <span className="num text-slate-200">{r.costBasis ? num(r.costBasis) : '—'}</span> },
            { key: 'bb', header: 'موجودی قبل', cell: (r: any) => <span className="num text-slate-400">{num(r.balanceBefore, 6)}</span> },
            {
              key: 'ba', header: 'موجودی بعد',
              cell: (r: any) => <span className="num font-semibold text-white">{num(r.balanceAfter, 6)}</span>,
            },
            {
              key: 'tx', header: 'شناسه تراکنش',
              cell: (r: any) => (
                <span className="num text-[11px] text-slate-500" dir="ltr" title={r.txid}>
                  {String(r.txid || '—').length > 18
                    ? `${String(r.txid).slice(0, 8)}…${String(r.txid).slice(-6)}`
                    : r.txid || '—'}
                </span>
              ),
            },
            { key: 'src', header: 'منبع معامله', cell: (r: any) => <Tag kind="muted">{r.source || '—'}</Tag> },
            { key: 'by', header: 'ایجادکننده', cell: (r: any) => <span className="text-[11px] text-slate-400">{r.operatorName || '—'}</span> },
            {
              key: 'st', header: 'وضعیت',
              cell: (r: any) => (
                <Tag kind={r.settled ? 'up' : r.status === 'rejected' ? 'down' : 'gold'}>
                  {r.statusLabel}
                </Tag>
              ),
            },
          ]}
        />
        <Pager meta={data?.meta} onPage={setPage} />
      </Panel>

      <p className="mt-3 px-1 text-[11px] leading-6 text-slate-500">
        <b className="text-slate-400">موجودی</b> مقدار کل آن دارایی نزد صرافی است و هر
        عملیاتی آن را جابه‌جا می‌کند. <b className="text-slate-400">بهای تمام‌شده</b> فقط با
        خرید و فروش واقعی حرکت می‌کند — واریز کاربر، دارایی امانی است و چون بابتش چیزی
        نپرداخته‌ایم، میانگین را رقیق نمی‌کند. ردیف‌های تسویه‌نشده دیده می‌شوند ولی
        موجودی را جابه‌جا نمی‌کنند.
      </p>
    </>
  )
}
